import { useRef, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const useScrollFadeIn = (options = {}, skip = false) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    // Reset on every navigation so items re-animate on the new page.
    setIsVisible(false);

    if (skip) return;
    const el = ref.current;
    if (!el) return;

    let positionObserver;
    let sizeObserver;

    const trigger = () => {
      setIsVisible(true);
      positionObserver?.unobserve(el);
      sizeObserver?.unobserve(el);
    };

    // On refresh with restored scroll position, items already scrolled past
    // are above the viewport — pre-render them so they're loaded.
    if (el.getBoundingClientRect().top < 0) {
      setIsVisible(true);
      return;
    }

    // Fires when the element's top edge crosses 10% above the bottom of the viewport.
    positionObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) trigger();
      },
      { threshold: 0, rootMargin: '0px 0px -10% 0px', ...options }
    );

    // Fires when enough of the element is visible — catches small components
    // that reach the size threshold before crossing the position threshold.
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    sizeObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) trigger();
      },
      { threshold: isMobile ? 0.05 : 0.6, ...options }
    );

    positionObserver.observe(el);
    sizeObserver.observe(el);

    return () => {
      positionObserver?.disconnect();
      sizeObserver?.disconnect();
    };
  }, [skip, pathname]);

  return { ref, isVisible, pathname };
};
